import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { isMessagesMenuItem, useMyAccountMenuItems } from '@/constants/menu';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { removeCookie } from '@/redux/modules/cookies';
import { trackNavClickedEvent } from '@/services/analytics/header';
import { COOKIE_SESSION_NAME, openSessionModal } from '@/utility/session';

import GlobalHeaderNavDestinations from './GlobalHeaderNavDestinations';
import GlobalHeaderNavMenuItem from './GlobalHeaderNavMenuItem/GlobalHeaderNavMenuItem';
import GlobalHeaderNavTravelJournal from './GlobalHeaderNavTravelJournal';

enum ESubmenus {
  DESTINATIONS = 'destinations',
  TRAVEL_JOURNAL = 'travel_journal',
}

interface IGlobalHeaderNavMobileMenuProps {
  isAutoPage?: boolean;
  userName?: string;
  isOwner?: boolean;
  unreadConversations?: number;
}

const GlobalHeaderNavMobileMenu: React.FC<
  React.PropsWithChildren<IGlobalHeaderNavMobileMenuProps>
> = ({ isAutoPage, userName, isOwner = false, unreadConversations }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { locale } = useLocaleSettings();
  const { logout } = useUser();

  const isAuthenticated = useSelector(selectAuthenticated);
  const items = useMyAccountMenuItems(isAutoPage);

  const [openSubmenu, setOpenSubmenu] = useState<ESubmenus | null>(null);

  const { pathname } = useRouter();
  const newBecomeAHostPage = pathname.includes('/become-a-host/');
  const isBAHLandingPage = pathname === '/become-a-host';
  const isBAHAutoPage = pathname === '/become-a-host/auto';

  const handleOpenSubmenu = (submenu: ESubmenus) => () => {
    setOpenSubmenu(submenu);
  };

  const handleCloseSubmenu = () => {
    setOpenSubmenu(null);
  };

  const handleClickLogin = () => {
    openSessionModal('login');
  };

  const handleClickSignup = () => {
    openSessionModal('signup');
  };

  const trackClickedEvent = (link: string) => {
    trackNavClickedEvent({
      clickedLink: link,
      isInDropdown: true,
      dropdownHeader: 'My Account',
    });
  };

  const becomeAHostText = () => {
    if (isAutoPage) {
      return intl.formatMessage({
        defaultMessage: 'List your Auto',
        id: 'LZKhke',
        description: 'Global Header Nav: Hosting flow link',
      });
    }

    return newBecomeAHostPage
      ? intl.formatMessage({
          defaultMessage: 'Try hosting today',
          id: '0Q7HS7',
          description: 'Global Header Nav: Hosting flow link',
        })
      : intl.formatMessage({
          defaultMessage: 'Become a host',
          id: '+ViN7X',
          description: 'Global Header Nav: List my RV link',
        });
  };

  const destinationsMenuItem = ['en-us', 'en-ca', 'fr-ca'].includes(locale) && (
    <GlobalHeaderNavMenuItem
      data-testid="destinations-btn"
      label={intl.formatMessage({
        defaultMessage: 'Destinations',
        id: 'nssesN',
        description: 'Global Header Nav: Destinations link',
      })}
      submenu={<GlobalHeaderNavDestinations />}
      onClick={handleOpenSubmenu(ESubmenus.DESTINATIONS)}
      onCloseSubmenu={handleCloseSubmenu}
      submenuOpen={openSubmenu === ESubmenus.DESTINATIONS}
      newNavMenuItem
    />
  );

  const travelJournalMenuItem = (
    <GlobalHeaderNavMenuItem
      data-testid="travel-journal-btn"
      label={intl.formatMessage({
        defaultMessage: 'Travel Journal',
        id: 'G/wCJJ',
        description: 'Global Header Nav: Travel Journal link',
      })}
      submenu={<GlobalHeaderNavTravelJournal />}
      onClick={handleOpenSubmenu(ESubmenus.TRAVEL_JOURNAL)}
      onCloseSubmenu={handleCloseSubmenu}
      submenuOpen={openSubmenu === ESubmenus.TRAVEL_JOURNAL}
      newNavMenuItem
    />
  );

  return (
    <nav className="h-full">
      <ul className="flex flex-col h-full" role="menubar">
        {isAuthenticated ? (
          <>
            <li className="mt-6 mb-6 font-bold text-gray-900 autoType600">
              {userName ? (
                <FormattedMessage
                  defaultMessage="Hi, {name}"
                  id="IjcYm5"
                  description="Global Header > My Account"
                  values={{ name: userName }}
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Hi"
                  id="Sd5efi"
                  description="Global Header > My Account"
                />
              )}
            </li>

            <li className="border-b border-gray-310" />

            {items.map(item => (
              <GlobalHeaderNavMenuItem
                key={item.label}
                label={item.label}
                href={item.href}
                onClick={() => trackClickedEvent(item.customEventName || item.label)}
                newNavMenuItem
                unreadConversations={isMessagesMenuItem(item) ? unreadConversations : undefined}
              />
            ))}

            <li className="border-b border-gray-310" />

            {!!(destinationsMenuItem || travelJournalMenuItem) &&
              !isBAHLandingPage &&
              !isAutoPage && (
                <>
                  {destinationsMenuItem}
                  {travelJournalMenuItem}
                  <li className="border-b border-gray-310" />
                </>
              )}

            {isOwner ? (
              <GlobalHeaderNavMenuItem
                href="/dashboard"
                label={intl.formatMessage({
                  defaultMessage: 'Switch to hosting',
                  id: '2dLHvx',
                  description: 'Global Header > My Account',
                })}
                onClick={() => trackClickedEvent('Dashboard')}
                newNavMenuItem
              />
            ) : (
              !isBAHLandingPage &&
              !isBAHAutoPage &&
              (newBecomeAHostPage ? (
                <GlobalHeaderNavMenuItem
                  label={intl.formatMessage({
                    defaultMessage: 'Try hosting today',
                    id: '73Bvs2',
                    description: 'Global Header > My Account',
                  })}
                  newNavMenuItem
                  newBecomeAHostPage={newBecomeAHostPage}
                  linkButton
                />
              ) : (
                <GlobalHeaderNavMenuItem
                  href={isAutoPage ? '/become-a-host/auto' : '/become-a-host'}
                  label={becomeAHostText()}
                  onClick={() => trackClickedEvent(isAutoPage ? 'List my Car' : 'List my RV')}
                  newNavMenuItem
                />
              ))
            )}

            <GlobalHeaderNavMenuItem
              label={intl.formatMessage({
                defaultMessage: 'Log out',
                id: 'Xne/lE',
                description: 'Global Header > My Account',
              })}
              onClick={() => {
                trackClickedEvent('Log out');

                logout();
                dispatch(removeCookie(COOKIE_SESSION_NAME));
              }}
              newNavMenuItem
            />
          </>
        ) : (
          <>
            {!!(destinationsMenuItem || travelJournalMenuItem) &&
              !isBAHLandingPage &&
              !isAutoPage && (
                <>
                  {destinationsMenuItem}
                  {travelJournalMenuItem}
                  <li className="border-b border-gray-310" />
                </>
              )}

            <GlobalHeaderNavMenuItem
              data-testid="login-btn"
              onClick={handleClickLogin}
              label={intl.formatMessage({
                defaultMessage: 'Log in',
                id: '5+5g80',
                description: 'Global Header Nav: Log in link',
              })}
              newNavMenuItem
            />

            {!isBAHLandingPage && !isBAHAutoPage && (
              <>
                <GlobalHeaderNavMenuItem
                  data-testid="signup-btn"
                  onClick={handleClickSignup}
                  label={intl.formatMessage({
                    defaultMessage: 'Sign up',
                    id: 'kZjbOt',
                    description: 'Global Header Nav: Sign up link',
                  })}
                  newNavMenuItem
                />

                <GlobalHeaderNavMenuItem
                  data-testid={isAutoPage ? 'list-my-car-btn' : 'list-my-rv-btn'}
                  href={isAutoPage ? '/become-a-host/auto' : '/become-a-host'}
                  label={becomeAHostText()}
                  asDesktopButton
                  newNavMenuItem
                  newBecomeAHostPage={newBecomeAHostPage}
                />
              </>
            )}
          </>
        )}
      </ul>
    </nav>
  );
};

export default GlobalHeaderNavMobileMenu;
